import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { barberService } from '../../services/barberService';
import { Barber } from '../../types/barber.types';
import { useToast } from '../../context/ToastContext';
import ConfirmModal from '../../components/common/ConfirmModal';
import AdminHeader from '../../components/admin/AdminHeader';

const BarberManagement: React.FC = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [barbers, setBarbers] = useState<Barber[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [barberToReset, setBarberToReset] = useState<number | null>(null);

    useEffect(() => {
        loadBarbers();
    }, []);

    const loadBarbers = async () => {
        try {
            setLoading(true);
            const data = await barberService.getAllBarbersAdmin();
            setBarbers(data || []);
            setError(null);
        } catch (err) {
            console.error('Error loading barbers:', err);
            setError('No se pudieron cargar los barberos. Por favor, intenta de nuevo.');
            showToast('Error al cargar la lista de barberos', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleWhatsAppClick = (phone?: string) => {
        if (!phone) {
            showToast('Este barbero no tiene WhatsApp configurado', 'warning');
            return;
        }
        // Remove all non-digit characters
        const cleanPhone = phone.replace(/\D/g, '');
        window.open(`https://wa.me/${cleanPhone}`, '_blank');
    };

    const handleResetPassword = (barberId: number) => {
        setBarberToReset(barberId);
        setShowConfirmModal(true);
    };

    const confirmReset = async () => {
        if (barberToReset === null) return;

        setShowConfirmModal(false);

        try {
            await barberService.resetPassword(barberToReset);
            // Find the barber email to show in the toast
            const barber = barbers.find(b => b.id === barberToReset);
            const email = barber?.email || 'el barbero';
            showToast(
                `Contraseña reseteada exitosamente. Las nuevas credenciales han sido enviadas a ${email}`,
                'success'
            );
        } catch (error) {
            console.error('Error resetting password:', error);
            showToast('Error al resetear la contraseña', 'error');
        } finally {
            setBarberToReset(null);
        }
    };

    const cancelReset = () => {
        setShowConfirmModal(false);
        setBarberToReset(null);
    };

    const handleReactivate = async (barberId: number) => {
        try {
            await barberService.reactivateBarber(barberId);
            showToast('Barbero reactivado exitosamente. Se envió un email con las nuevas credenciales.', 'success');
            loadBarbers();
        } catch (error: any) {
            console.error('Error reactivating barber:', error);
            showToast(error.response?.data?.message || 'Error al reactivar el barbero', 'error');
        }
    };



    return (
        <>
            <AdminHeader
                title="✂️ Gestión de Barberos"
                rightContent={
                    <button className="btn btn-primary btn-sm" onClick={() => navigate('/admin/create-barber')}>
                        ➕ Crear Barbero
                    </button>
                }
            />
            <div className="container fade-in">

                {loading ? (
                    <div className="card p-xl text-center">
                        <div className="spinner m-auto mb-md"></div>
                        <p>Cargando barberos...</p>
                    </div>
                ) : error ? (
                    <div className="card p-xl text-center border-error bg-error-light">
                        <p className="text-error mb-md">{error}</p>
                        <button className="btn btn-outline btn-sm" onClick={loadBarbers}>Reintentar</button>
                    </div>
                ) : (barbers && barbers.length === 0) ? (
                    <div className="card p-xl text-center">
                        <p className="text-secondary mb-md">No hay barberos registrados aún.</p>
                        <button className="btn btn-primary" onClick={() => navigate('/admin/create-barber')}>
                            Crear primer barbero
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="responsive-grid">
                            {barbers.map((barber) => (
                                <div key={barber.id} className="card p-md shadow-sm hover:shadow-md transition-all flex flex-col" style={{
                                    background: '#111111',
                                    borderRadius: '1rem',
                                    border: '1px solid #2a2a2a',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                                }}>
                                    {/* Status badges */}
                                    <div className="flex justify-end gap-sm mb-sm">
                                        <span style={{
                                            fontSize: '0.6rem',
                                            padding: '0.25rem 0.6rem',
                                            borderRadius: '1rem',
                                            fontWeight: '700',
                                            background: barber.isIndependent ? '#3b82f620' : '#8b5cf620',
                                            color: barber.isIndependent ? '#3b82f6' : '#8b5cf6',
                                            border: `1px solid ${barber.isIndependent ? '#3b82f640' : '#8b5cf640'}`
                                        }}>
                                            {barber.isIndependent ? 'INDEPENDIENTE' : 'STAFF'}
                                        </span>
                                        <span style={{
                                            fontSize: '0.6rem',
                                            padding: '0.25rem 0.6rem',
                                            borderRadius: '1rem',
                                            fontWeight: '700',
                                            background: barber.status === 'ACTIVE' ? '#22c55e20' : '#ef444420',
                                            color: barber.status === 'ACTIVE' ? '#22c55e' : '#ef4444',
                                            border: `1px solid ${barber.status === 'ACTIVE' ? '#22c55e40' : '#ef444440'}`
                                        }}>
                                            {barber.status === 'ACTIVE' ? 'ACTIVO' : 'INACTIVO'}
                                        </span>
                                    </div>

                                    {/* Avatar and Info */}
                                    <div className="flex flex-col items-center text-center mb-md">
                                        <div className="relative mb-sm">
                                            <div
                                                style={{
                                                    width: '64px',
                                                    height: '64px',
                                                    minWidth: '64px',
                                                    minHeight: '64px',
                                                    maxWidth: '64px',
                                                    maxHeight: '64px',
                                                    borderRadius: '50%',
                                                    backgroundColor: barber.profilePicture ? 'transparent' : '#f59e0b',
                                                    color: barber.profilePicture ? 'inherit' : '#000',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: 'bold',
                                                    fontSize: '1.5rem',
                                                    overflow: 'hidden',
                                                    border: '2px solid #f59e0b40',
                                                    boxShadow: '0 0 15px rgba(245, 158, 11, 0.2)'
                                                }}>
                                                {barber.profilePicture ? (
                                                    <img
                                                        src={barber.profilePicture.startsWith('http') || barber.profilePicture.startsWith('data:')
                                                            ? barber.profilePicture
                                                            : `/api${barber.profilePicture.startsWith('/') ? '' : '/'}${barber.profilePicture}`
                                                        }
                                                        alt={`${barber.firstName} ${barber.lastName}`}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            borderRadius: '50%'
                                                        }}
                                                        onError={(e) => {
                                                            // Si falla la carga, mostrar iniciales
                                                            e.currentTarget.style.display = 'none';
                                                            const parent = e.currentTarget.parentElement;
                                                            if (parent) {
                                                                parent.style.backgroundColor = '#f59e0b';
                                                                parent.style.color = '#000';
                                                                parent.innerHTML = `${barber.firstName.charAt(0)}${barber.lastName.charAt(0)}`;
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    `${barber.firstName.charAt(0)}${barber.lastName.charAt(0)}`
                                                )}
                                            </div>
                                            {barber.status === 'ACTIVE' && (
                                                <div className="absolute bottom-0 right-0 w-5 h-5 bg-success rounded-full border-2" style={{ borderColor: '#111111' }}></div>
                                            )}
                                        </div>

                                        <p className="text-accent font-bold text-lg mb-xs" style={{ color: '#f59e0b' }}>"{barber.nickname}"</p>
                                        <h3 className="m-0 font-semibold text-base mb-xs" style={{ color: '#e5e5e5' }}>
                                            {barber.firstName} {barber.lastName}
                                        </h3>
                                        <p className="text-sm mb-sm" style={{ color: '#737373' }}>
                                            📞 {barber.whatsapp || 'Sin teléfono'}
                                        </p>

                                        {/* WhatsApp Button */}
                                        {barber.whatsapp && (
                                            <button
                                                className="btn btn-sm btn-outline gap-xs mb-md"
                                                style={{
                                                    backgroundColor: '#25D366',
                                                    color: 'white',
                                                    border: 'none',
                                                    fontSize: '0.75rem',
                                                    padding: '0.4rem 0.8rem'
                                                }}
                                                onClick={() => handleWhatsAppClick(barber.whatsapp)}
                                            >
                                                💬 Contactar por WhatsApp
                                            </button>
                                        )}
                                    </div>

                                    {/* Action Buttons */}
                                    {barber.status === 'INACTIVE' ? (
                                        <div className="mt-auto">
                                            <button
                                                className="btn btn-sm font-bold w-full"
                                                style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', border: 'none', color: '#fff', borderRadius: '0.5rem', fontSize: '0.75rem', padding: '0.6rem' }}
                                                onClick={() => handleReactivate(barber.id)}
                                            >
                                                🔄 Reactivar Barbero
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-3 gap-sm mt-auto">
                                            <button
                                                className="btn btn-sm font-bold"
                                                style={{ background: 'transparent', border: '1px solid #404040', color: '#a3a3a3', borderRadius: '0.5rem', fontSize: '0.75rem' }}
                                                onClick={() => navigate(`/admin/barbers/${barber.id}/view`)}
                                            >
                                                👤 Perfil
                                            </button>
                                            <button
                                                className="btn btn-sm font-bold"
                                                style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', color: '#000', borderRadius: '0.5rem', fontSize: '0.75rem' }}
                                                onClick={() => navigate(`/admin/barbers/${barber.id}/schedule`)}
                                            >
                                                📅 Horarios
                                            </button>
                                            <button
                                                className="btn btn-sm font-bold"
                                                style={{ background: 'transparent', border: '1px solid #404040', color: '#a3a3a3', borderRadius: '0.5rem', fontSize: '0.75rem' }}
                                                onClick={() => handleResetPassword(barber.id)}
                                                title="Resetear contraseña"
                                            >
                                                🔐 Reset
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="text-center text-sm text-secondary mt-xl pb-3xl opacity-50 font-medium">
                            Total: {barbers.length} barbero{barbers.length !== 1 ? 's' : ''} registrado{barbers.length !== 1 ? 's' : ''}
                        </div>
                    </>
                )}

                {/* Confirm Reset Modal */}
                <ConfirmModal
                    isOpen={showConfirmModal}
                    title="Confirmar Reset de Contraseña"
                    message="¿Estás seguro de que deseas resetear la contraseña de este barbero? Se generará una nueva contraseña automáticamente."
                    onConfirm={confirmReset}
                    onCancel={cancelReset}
                    confirmText="Resetear"
                    cancelText="Cancelar"
                />


            </div>
        </>
    );
};

export default BarberManagement;
